import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Lock, User } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { API_BASE as API } from '../lib/apiBase';


export default function ActivateAccountPage() {
  const { lang } = useLang();
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: '', password: '', confirmPassword: '' });

  useEffect(() => {
    if (!token) {
      setError(lang === 'es' ? 'Falta el token de acceso.' : 'Missing access token.');
      setLoading(false);
      return;
    }

    fetch(`${API}/api/auth/access-link-status?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Error');
        setAccount(data);
        setForm((prev) => ({ ...prev, name: data.name || '' }));
      })
      .catch((err) => setError(err.message || 'Error'))
      .finally(() => setLoading(false));
  }, [token, lang]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      setError(lang === 'es' ? 'La contraseña debe tener al menos 8 caracteres.' : 'Password must be at least 8 characters.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError(lang === 'es' ? 'Las contraseñas no coinciden.' : 'Passwords do not match.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/activate-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, name: form.name, password: form.password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      localStorage.setItem('pulse_token', data.token);
      localStorage.setItem('pulse_refresh', data.refresh_token);
      loginWithToken(data.token, data.user);
      setSuccess(true);
      setTimeout(() => navigate(data.redirect_to || '/'), 900);
    } catch (err) {
      setError(err.message || 'Error');
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="activate-account-page" className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" data-testid="activate-back-login" className="inline-flex items-center gap-2 text-pulse-muted text-sm mb-6 hover:text-white transition-colors">
          <ArrowLeft size={16} /> {lang === 'es' ? 'Volver al acceso' : 'Back to access'}
        </Link>

        <div className="bg-pulse-surface border border-pulse-border rounded-2xl p-8">
          <h1 className="font-heading font-bold text-2xl text-center">{lang === 'es' ? 'Activa tu cuenta' : 'Activate your account'}</h1>

          {loading ? (
            <div className="py-10 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : success ? (
            <div data-testid="activate-account-success" className="mt-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto">
                <CheckCircle size={30} className="text-green-400" />
              </div>
              <p className="text-sm">{lang === 'es' ? 'Cuenta activada. Entrando…' : 'Account activated. Entering…'}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 mt-8">
              {account && (
                <div data-testid="activate-account-summary" className="rounded-xl bg-pulse-elevated border border-pulse-border p-4 text-sm">
                  <p className="font-medium">{account.email}</p>
                  <p className="text-xs text-pulse-muted mt-1">
                    {lang === 'es'
                      ? 'Verifica tu identidad y define la contraseña de acceso.'
                      : 'Verify your identity and set your access password.'}
                  </p>
                </div>
              )}

              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-pulse-muted" />
                <input
                  data-testid="activate-account-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={lang === 'es' ? 'Nombre visible' : 'Display name'}
                  className="w-full bg-pulse-elevated border border-pulse-border rounded-xl pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                  required
                />
              </div>

              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-pulse-muted" />
                <input
                  data-testid="activate-account-password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  placeholder={lang === 'es' ? 'Nueva contraseña' : 'New password'}
                  minLength={8}
                  className="w-full bg-pulse-elevated border border-pulse-border rounded-xl pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                  required
                />
              </div>

              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-pulse-muted" />
                <input
                  data-testid="activate-account-confirm-password"
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder={lang === 'es' ? 'Repite la contraseña' : 'Repeat password'}
                  minLength={8}
                  className="w-full bg-pulse-elevated border border-pulse-border rounded-xl pl-11 pr-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                  required
                />
              </div>

              {error && <p data-testid="activate-account-error" className="text-red-400 text-xs">{error}</p>}

              <button
                data-testid="activate-account-submit"
                type="submit"
                disabled={submitting || !token}
                className="w-full bg-pulse-primary text-black py-3 rounded-xl font-bold text-sm disabled:opacity-50"
              >
                {submitting ? '...' : (lang === 'es' ? 'Verificar y crear contraseña' : 'Verify and create password')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}